/**
 * 单一 I/O 出口（骨架，#89098）
 *
 * 四个模块**共用这一个 fetch**。publishableKey 注入、自签 session 注入、超时策略
 * 都只在这里实现一次 —— 任何模块自己拼 fetch 都是架构错误：
 * 会长出多套鉴权发送方式，而 §5.2 对此有唯一规定。
 *
 * 契约来源：`services/cmd/agentserver/docs/genie-baas/genie-baas-phase1-design.md` §5.2 / §6.0
 */

import type { CloudRuntimeConfig } from '../types.js';

/**
 * publishableKey 的请求头形态。
 *
 * §5.2 规定服务端同时接受两种发送方式：`clientId` 位置（TCB 语义，body/query）
 * 与本头部。SDK 统一走头部 —— 它对 GET/POST 一致，不需要按方法改写 body。
 *
 * 头名用 genie-baas 自己的品牌（`x-wb-webapp-access-key`）而非 TCB 的
 * `x-cloudbase-access-key`：genie-baas 在服务端做了凭据换签，浏览器端根本
 * 不直接对话 CloudBase，暴露 cloudbase 品牌名没有意义、也泄漏底层实现。
 * 服务端 gateway 认这个新头（见 dataplane_proxy.go 的 accessKeyHeader）。
 */
export const PUBLISHABLE_KEY_HEADER = 'x-wb-webapp-access-key';

/**
 * 已登录终端用户的自签 session token 提供者。
 *
 * 返回 undefined 表示未登录 —— 此时按匿名身份转发（§6.1），**不是错误**。
 * 用函数而非固定值：登录/登出/刷新后无需重建 client，database、storage
 * 自动带上最新身份。
 *
 * ---------------------------------------------------------------------------
 * 为什么是异步（这一点不是可选的）
 *
 * 因为「取 token」这个动作**可能要先发一次网络请求** —— token 已过期或临近
 * 过期时必须先续期再发业务请求。同步签名下做不到这件事：
 *
 *  - 同步 XHR 发续期请求：浏览器已废弃，且阻塞主线程。
 *  - 只靠后台定时器续期、provider 同步读缓存：**会漏**。后台标签页的
 *    setInterval 被节流到分钟级，笔记本休眠唤醒后 token 早已过期。
 *    定时器只能算优化，请求前检查才是正确性底线。
 *  - 返回过期 token、靠 401 重试：见 AuthModule 里关于句柄轮换的说明 ——
 *    我方句柄一旦轮换旧的立即失效，N 个并发 401 各自去刷会把会话弄坏。
 *
 * 改异步对 vendor 完全无感：它只知道自己拿到一个返回 Promise 的 fetch
 * （`src/vendor/postgrest/PostgrestBuilder.ts` 里是 `await _fetch(...)`）。
 * ---------------------------------------------------------------------------
 */
export type SessionTokenProvider = () => Promise<string | undefined>;

/**
 * 包装出四模块共用的 fetch。
 *
 * ⚠️ 注入的是**我方自签 session token**，不是 CloudBase 的 token ——
 * 真实 provider 凭据只存服务端，浏览器永远拿不到（§5.3）。
 *
 * ⚠️ auth 模块**自己的**请求不能用带 provider 的 fetch：`/auth/v1/token`
 * 本身就是续期端点，让它去调「取 token（必要时续期）」会死锁。装配见 client.ts。
 */
export function createCloudFetch(
    config: CloudRuntimeConfig,
    getSessionToken: SessionTokenProvider,
): typeof globalThis.fetch {
    return async (input, init) => {
        const headers = new Headers(init?.headers);

        // 应用身份：标识「哪个应用」，不携带权限（§5.2）。
        headers.set(PUBLISHABLE_KEY_HEADER, config.publishableKey);

        // 终端用户身份：有则带，无则匿名。
        //
        // 已有 Authorization 时不覆盖：调用方显式指定身份的场景（例如刻意用
        // 别的 token 做越权验证）必须能压过默认注入。
        if (!headers.has('Authorization')) {
            const token = await getSessionToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
        }

        return config.fetch(input, { ...init, headers });
    };
}

/** 恒定匿名的 provider，供 auth 模块自身使用（见 createCloudFetch 的死锁说明）。 */
export const anonymousTokenProvider: SessionTokenProvider = () => Promise.resolve(undefined);
